import type { BigFraction } from "./math/BigFraction.js";
import type { CurrencyCode } from "./shapes/Currency.js";

type CurrencyDisplay = "narrowSymbol" | "symbol";

export const formatArea = (value: BigFraction, lengthUnitSymbol: string) =>
  `${formatDecimalNumber(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })} ${lengthUnitSymbol}²`;

export const formatIntegerNumber = (
  locales: Intl.LocalesArgument,
  value: number | BigFraction,
) => value.valueOf().toLocaleString(locales, { maximumFractionDigits: 0 });

/**
 * @return the number formatted according to the current runtime local with
 * two digits for the decimal part by default.
 */
export const formatDecimalNumber = (
  value: number | BigFraction,
  options?: Intl.NumberFormatOptions,
) =>
  value.valueOf().toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
    // cf. https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/NumberFormat/NumberFormat#negative
    // TS definition need to catch up. When
    // https://github.com/microsoft/TypeScript/pull/56902 lands we will at least
    // be able to extend `signDisplay`'s type thanks to declaration merging with
    // `NumberFormatOptionsSignDisplayRegistry`.
    signDisplay: "negative" as Intl.NumberFormatOptions["signDisplay"],
    ...options,
  });

export const formatCurrencyAmount = (value: BigFraction, currency: string) =>
  formatDecimalNumber(value, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
  });

/**
 * Formats the given currency value in parentheses when negative.
 *
 * This is a common practice in accounting.
 *
 * `-100` → `(100)`
 */
export const formatAccountingCurrencyAmount = (
  value: BigFraction,
  currency: CurrencyCode,
) =>
  formatDecimalNumber(value, {
    style: "currency",
    currency,
    currencyDisplay: "narrowSymbol",
    currencySign: "accounting",
  });

/**
 * Formats currency amount using abbreviation for large numbers (thousands,
 * millions, billions and trillions)
 */
export const abbreviateNumber = (
  locale: Intl.LocalesArgument,
  value: BigFraction,
  options?: Intl.NumberFormatOptions,
) =>
  value.valueOf().toLocaleString(locale, {
    notation: "compact",
    maximumFractionDigits: 1,
    trailingZeroDisplay: "stripIfInteger",
    ...options,
  });

export const abbreviateCurrencyAmount = (
  locale: Intl.LocalesArgument,
  value: BigFraction,
  options: {
    currency: string;
    currencyDisplay?: CurrencyDisplay;
  },
) =>
  abbreviateNumber(locale, value, {
    style: "currency",
    currency: options.currency,
    currencyDisplay: options.currencyDisplay ?? "narrowSymbol",
  });

export const formatPercentage = (value: BigFraction, round = false) =>
  formatDecimalNumber(value, {
    style: "percent",
    ...(round && {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }),
  });
